import React, { useState, useEffect } from 'react';
import { ref, push, set, remove, onValue, off } from 'firebase/database';
import { managementDb } from '../firebase';
import { toast } from 'react-toastify';
import './HomeManagement.css';

const HomeManagement = () => {
  const [openingVideos, setOpeningVideos] = useState([]);
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingVideo, setEditingVideo] = useState(null);
  const [activeSection, setActiveSection] = useState('opening'); // 'opening' or 'featured'
  const [formData, setFormData] = useState({
    title: '',
    videoUrl: '',
    description: '',
    isActive: false,
    order: 0
  });

  // Helper function to extract YouTube video ID
  const getYouTubeVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      return match[2];
    }
    return null;
  };

  // Fetch opening videos from Firebase
  useEffect(() => {
    const openingVideosRef = ref(managementDb, 'home/opening-videos');
    
    console.log('🔍 Fetching opening videos from Firebase...');
    
    const fetchOpeningVideos = onValue(openingVideosRef, (snapshot) => {
      console.log('📥 Opening videos snapshot received:', snapshot.val());
      const data = snapshot.val();
      if (data) {
        const videosArray = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        })).sort((a, b) => a.order - b.order);
        setOpeningVideos(videosArray);
        console.log('✅ Opening videos loaded successfully:', videosArray);
      } else {
        setOpeningVideos([]);
        console.log('📭 No opening videos found in database');
      }
    }, (error) => {
      console.error('❌ Error fetching opening videos:', error);
      toast.error(`Failed to load opening videos: ${error.message}`);
    });

    return () => off(openingVideosRef, 'value', fetchOpeningVideos);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch featured videos from Firebase
  useEffect(() => {
    const featuredVideosRef = ref(managementDb, 'home/featured-videos');
    
    console.log('🔍 Fetching featured videos from Firebase...');
    
    const fetchFeaturedVideos = onValue(featuredVideosRef, (snapshot) => {
      console.log('📥 Featured videos snapshot received:', snapshot.val());
      const data = snapshot.val();
      if (data) {
        const videosArray = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        })).sort((a, b) => a.order - b.order);
        setFeaturedVideos(videosArray);
        console.log('✅ Featured videos loaded successfully:', videosArray);
      } else {
        setFeaturedVideos([]);
        console.log('📭 No featured videos found in database');
      }
      setLoading(false);
    }, (error) => {
      console.error('❌ Error fetching featured videos:', error);
      toast.error(`Failed to load featured videos: ${error.message}`);
      setLoading(false);
    });

    return () => off(featuredVideosRef, 'value', fetchFeaturedVideos);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Get current videos based on active section
  const getCurrentVideos = () => {
    return activeSection === 'opening' ? openingVideos : featuredVideos;
  };

  // Get database path based on active section
  const getDatabasePath = () => {
    return activeSection === 'opening' ? 'home/opening-videos' : 'home/featured-videos';
  };

  // Add new video
  const handleAddVideo = async (e) => {
    e.preventDefault();
    
    console.log('➕ Adding video with data:', formData);
    
    if (!formData.title.trim() || !formData.videoUrl.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const videosRef = ref(managementDb, getDatabasePath());
      const newVideoRef = push(videosRef);
      
      const videoData = {
        ...formData,
        order: getCurrentVideos().length + 1,
        createdAt: Date.now()
      };
      
      console.log('💾 Saving video data:', videoData);
      
      await set(newVideoRef, videoData);
      console.log('✅ Video saved successfully');

      // If the new video is active, deactivate all other videos in the same section
      if (formData.isActive) {
        const deactivatePromises = getCurrentVideos().map(v => {
          const videoRef = ref(managementDb, `${getDatabasePath()}/${v.id}`);
          return set(videoRef, {
            ...v,
            isActive: false,
            updatedAt: Date.now()
          });
        });
        
        await Promise.all(deactivatePromises);
        toast.success('Video added and activated! All other videos in this section have been deactivated.');
      } else {
        toast.success('Video added successfully!');
      }

      setFormData({
        title: '',
        videoUrl: '',
        description: '',
        isActive: false,
        order: 0
      });
    } catch (error) {
      console.error('❌ Error adding video:', error);
      toast.error(`Failed to add video: ${error.message}`);
    }
  };

  // Edit video
  const handleEditVideo = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.videoUrl.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const videoRef = ref(managementDb, `${getDatabasePath()}/${editingVideo.id}`);
      
      await set(videoRef, {
        ...formData,
        updatedAt: Date.now()
      });

      // If the edited video is being activated, deactivate all other videos in the same section
      if (formData.isActive && !editingVideo.isActive) {
        const deactivatePromises = getCurrentVideos().map(v => {
          if (v.id !== editingVideo.id) {
            const otherVideoRef = ref(managementDb, `${getDatabasePath()}/${v.id}`);
            return set(otherVideoRef, {
              ...v,
              isActive: false,
              updatedAt: Date.now()
            });
          }
          return Promise.resolve();
        });
        
        await Promise.all(deactivatePromises);
        toast.success('Video updated and activated! All other videos in this section have been deactivated.');
      } else {
        toast.success('Video updated successfully!');
      }

      setEditingVideo(null);
      setFormData({
        title: '',
        videoUrl: '',
        description: '',
        isActive: false,
        order: 0
      });
    } catch (error) {
      console.error('Error updating video:', error);
      toast.error(`Failed to update video: ${error.message}`);
    }
  };

  // Delete video
  const handleDeleteVideo = async (videoId) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      try {
        const videoRef = ref(managementDb, `${getDatabasePath()}/${videoId}`);
        await remove(videoRef);
        toast.success('Video deleted successfully!');
      } catch (error) {
        console.error('Error deleting video:', error);
        toast.error(`Failed to delete video: ${error.message}`);
      }
    }
  };

  // Start editing video
  const startEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      videoUrl: video.videoUrl,
      description: video.description || '',
      isActive: video.isActive || false,
      order: video.order || 0
    });
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingVideo(null);
    setFormData({
      title: '',
      videoUrl: '',
      description: '',
      isActive: false,
      order: 0
    });
  };

  // Toggle video active status
  const toggleActiveStatus = async (video) => {
    try {
      // If we're activating this video, deactivate all others in the same section first
      if (!video.isActive) {
        // Deactivate all videos in the same section first
        const deactivatePromises = getCurrentVideos().map(v => {
          if (v.id !== video.id) {
            const videoRef = ref(managementDb, `${getDatabasePath()}/${v.id}`);
            return set(videoRef, {
              ...v,
              isActive: false,
              updatedAt: Date.now()
            });
          }
          return Promise.resolve();
        });
        
        await Promise.all(deactivatePromises);
      }
      
      // Now toggle the current video's status
      const videoRef = ref(managementDb, `${getDatabasePath()}/${video.id}`);
      await set(videoRef, {
        ...video,
        isActive: !video.isActive,
        updatedAt: Date.now()
      });
      
      toast.success(`Video ${!video.isActive ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Error toggling video status:', error);
      toast.error(`Failed to update video status: ${error.message}`);
    }
  };

  if (loading) {
    return null;
  }

  return (
    <div>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-home me-2"></i>
                Home Management
              </h3>
            </div>
            <div className="card-body">
              {/* Section Dropdown */}
              <div className="section-dropdown mb-4">
                <div className="dropdown">
                  <button 
                    className="btn btn-outline-primary dropdown-toggle" 
                    type="button" 
                    id="sectionDropdown" 
                    data-bs-toggle="dropdown" 
                    aria-expanded="false"
                    style={{ minWidth: '200px' }}
                  >
                    <i className={`fas ${activeSection === 'opening' ? 'fa-play-circle' : 'fa-star'} me-2`}></i>
                    {activeSection === 'opening' ? 'Opening Video' : 'Featured Video'}
                  </button>
                  <ul className="dropdown-menu" aria-labelledby="sectionDropdown">
                    <li>
                      <button
                        className={`dropdown-item ${activeSection === 'opening' ? 'active' : ''}`}
                        onClick={() => {
                          setActiveSection('opening');
                          setEditingVideo(null);
                          setFormData({
                            title: '',
                            videoUrl: '',
                            description: '',
                            isActive: false,
                            order: 0
                          });
                        }}
                      >
                        <i className="fas fa-play-circle"></i>
                        <span>Opening Video</span>
                        {activeSection === 'opening' && <i className="fas fa-check"></i>}
                      </button>
                    </li>
                    <li>
                      <button
                        className={`dropdown-item ${activeSection === 'featured' ? 'active' : ''}`}
                        onClick={() => {
                          setActiveSection('featured');
                          setEditingVideo(null);
                          setFormData({
                            title: '',
                            videoUrl: '',
                            description: '',
                            isActive: false,
                            order: 0
                          });
                        }}
                      >
                        <i className="fas fa-star"></i>
                        <span>Featured Video</span>
                        {activeSection === 'featured' && <i className="fas fa-check"></i>}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Add/Edit Form */}
              <div className="form-section mb-4">
                <div className="row">
                  {/* Left: Form */}
                  <div className="col-lg-6">
                    <h5 className="form-title">
                      {editingVideo ? 'Edit Video' : `Add New ${activeSection === 'opening' ? 'Opening' : 'Featured'} Video`}
                    </h5>
                    <form onSubmit={editingVideo ? handleEditVideo : handleAddVideo}>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label htmlFor="title" className="form-label">
                              Video Title <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="title"
                              name="title"
                              value={formData.title}
                              onChange={handleInputChange}
                              placeholder="Enter video title"
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label htmlFor="videoUrl" className="form-label">
                              Video URL <span className="text-danger">*</span>
                            </label>
                            <input
                              type="url"
                              className="form-control"
                              id="videoUrl"
                              name="videoUrl"
                              value={formData.videoUrl}
                              onChange={handleInputChange}
                              placeholder="https://example.com/video"
                              required
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <label htmlFor="description" className="form-label">
                          Description
                        </label>
                        <textarea
                          className="form-control"
                          id="description"
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          rows="3"
                          placeholder="Enter video description (optional)"
                          required
                        />
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label htmlFor="order" className="form-label">
                              Display Order
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              id="order"
                              name="order"
                              value={formData.order}
                              onChange={handleInputChange}
                              min="1"
                              placeholder="1"
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="isActive"
                                name="isActive"
                                checked={formData.isActive}
                                onChange={handleInputChange}
                              />
                              <label className="form-check-label" htmlFor="isActive">
                                Active (Show on website)
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="form-actions">
                        {editingVideo ? (
                          <>
                            <button type="submit" className="btn btn-primary me-2">
                              <i className="fas fa-save me-1"></i>
                              Update Video
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={cancelEdit}
                            >
                              <i className="fas fa-times me-1"></i>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button type="submit" className="btn btn-success">
                            <i className="fas fa-plus me-1"></i>
                            Add Video
                          </button>
                        )}
                      </div>
                    </form>
                  </div>

                  {/* Right: Active Video Preview */}
                  <div className="col-lg-6">
                    <div className="active-video-preview">
                      <h6 className="preview-title">
                        <i className="fas fa-play-circle me-2"></i>
                        Currently Active {activeSection === 'opening' ? 'Opening' : 'Featured'} Video
                      </h6>
                      
                      {getCurrentVideos().find(v => v.isActive) ? (
                        <div className="video-preview-container">
                          <div className="video-preview-wrapper">
                            <iframe
                              src={`https://www.youtube.com/embed/${getYouTubeVideoId(getCurrentVideos().find(v => v.isActive).videoUrl)}`}
                              title={getCurrentVideos().find(v => v.isActive).title}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              frameBorder="0"
                            />
                          </div>
                          <div className="video-preview-info">
                            <h6 className="video-preview-title">
                              {getCurrentVideos().find(v => v.isActive).title}
                            </h6>
                            {getCurrentVideos().find(v => v.isActive).description && (
                              <p className="video-preview-description">
                                {getCurrentVideos().find(v => v.isActive).description}
                              </p>
                            )}
                            <div className="video-preview-meta">
                              <span className="badge bg-success me-2">
                                <i className="fas fa-check-circle me-1"></i>
                                Active
                              </span>
                              <span className="badge bg-primary">
                                Order #{getCurrentVideos().find(v => v.isActive).order}
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="no-active-video">
                          <div className="no-video-placeholder">
                            <i className="fas fa-video-slash"></i>
                            <p>No active {activeSection === 'opening' ? 'opening' : 'featured'} video</p>
                            <small className="text-muted">
                              The {activeSection === 'opening' ? 'hero section' : 'featured work section'} will show a placeholder until you activate a video.
                            </small>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Videos List */}
              <div className="videos-section">
                <h5 className="section-title" style={{fontSize: '1.25rem'}}>
                  <i className="fas fa-list me-2"></i>
                  {activeSection === 'opening' ? 'Opening' : 'Featured'} Video Links ({getCurrentVideos().length})
                </h5>
                
                {getCurrentVideos().length === 0 ? (
                  <div className="no-videos">
                    <i className="fas fa-video text-muted"></i>
                    <p>No {activeSection === 'opening' ? 'opening' : 'featured'} videos added yet. Add your first video above.</p>
                    <small className="text-muted">
                      If you've added videos but they're not showing here, check the console for errors.
                    </small>
                  </div>
                ) : (
                  <div className="videos-grid">
                    {getCurrentVideos().map((video) => (
                      <div key={video.id} className={`video-card ${video.isActive ? 'active' : 'inactive'}`}>
                        <div className="video-header">
                          <div className="video-status">
                            <span className={`status-badge ${video.isActive ? 'active' : 'inactive'}`}>
                              {video.isActive ? 'Active' : 'Inactive'}
                            </span>
                            <span className="order-badge">#{video.order}</span>
                          </div>
                          <div className="video-actions">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => startEdit(video)}
                              title="Edit"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => toggleActiveStatus(video)}
                              title={video.isActive ? 'Deactivate' : 'Activate'}
                            >
                              <i className={`fas ${video.isActive ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteVideo(video.id)}
                              title="Delete"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                        
                        <div className="video-content">
                          <h6 className="video-title">{video.title}</h6>
                          {video.description && (
                            <p className="video-description">{video.description}</p>
                          )}
                          <div className="video-url">
                            <a 
                              href={video.videoUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="url-link"
                            >
                              <i className="fas fa-external-link-alt me-1"></i>
                              {video.videoUrl}
                            </a>
                          </div>
                        </div>
                        
                        <div className="video-footer">
                          <small className="text-muted">
                            Created: {new Date(video.createdAt).toLocaleDateString()}
                            {video.updatedAt && (
                              <span className="ms-2">
                                | Updated: {new Date(video.updatedAt).toLocaleDateString()}
                              </span>
                            )}
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeManagement;